import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeftRight, Truck, UserRound } from 'lucide-react';
import { api } from '../../api/client';
import { useAuth, type SessionUser } from '../../state/auth';
import { useLanguage } from '../../i18n/LanguageProvider';
import { Button } from '../Button/Button';
import './role-settings-card.css';

type Role = 'customer' | 'carrier';
type Profile = {
  customer_type?: 'person' | 'company'; carrier_type?: 'person' | 'company';
  first_name: string; last_name: string; company_name: string; contact_name: string;
};
type Profiles = Record<Role, Profile | null>;
const emptyNames = { first_name: '', last_name: '', company_name: '', contact_name: '' };

export function RoleSettingsCard() {
  const auth = useAuth();
  const { language } = useLanguage();
  const en = language === 'en';
  const createRequest = new URLSearchParams(window.location.hash.split('?')[1]).get('intent') === 'create-request';
  const [profiles, setProfiles] = useState<Profiles | null>(null);
  const [target, setTarget] = useState<Role | null>(null);
  const [accountType, setAccountType] = useState<'person' | 'company'>('person');
  const [names, setNames] = useState(emptyNames);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const label = (ru: string, english: string) => en ? english : ru;

  async function loadProfiles() {
    setError('');
    try { setProfiles(await api<Profiles>('/api/profiles/me/')); }
    catch { setError(label('Не удалось загрузить профили. Попробуйте ещё раз.', 'Could not load profiles. Please try again.')); }
  }
  useEffect(() => { void loadProfiles(); }, []);
  useEffect(() => {
    if (!retryAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [retryAt]);

  function finish(role: Role) {
    window.location.hash = role === 'customer' && createRequest ? 'new-request-step-1' : role === 'carrier' ? 'carrier-settings' : 'settings';
  }
  async function choose(role: Role) {
    if (!profiles || busy) return;
    setError('');
    if (target && role === auth.user?.active_role) {
      setTarget(null);
      return;
    }
    if (role === target) return;
    const existing = profiles[role];
    const source = existing ?? profiles[auth.user?.active_role === 'carrier' ? 'carrier' : 'customer'];
    const type = source?.customer_type ?? source?.carrier_type ?? 'person';
    const data = { ...emptyNames, ...source };
    const complete = type === 'company' ? data.company_name.trim() && data.contact_name.trim() : data.first_name.trim() && data.last_name.trim();
    if (existing && complete) {
      setBusy(true);
      try { await auth.switchRole(role); finish(role); }
      catch { setError(label('Не удалось сменить роль. Попробуйте ещё раз.', 'Could not switch roles. Please try again.')); }
      finally { setBusy(false); }
      return;
    }
    setAccountType(type);
    setNames(data);
    setTarget(role);
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!target || !profiles || busy) return;
    setBusy(true); setError('');
    try {
      const required = accountType === 'company' ? ['company_name', 'contact_name'] as const : ['first_name', 'last_name'] as const;
      if (required.some((key) => !names[key].trim())) throw new Error(label('Заполните обязательные поля.', 'Please fill in the required fields.'));
      const body = Object.fromEntries(required.map((key) => [key, names[key].trim()]));
      if (profiles[target]) {
        await api(`/api/profiles/me/${target}/`, { method: 'PATCH', body });
        await auth.switchRole(target);
      } else {
        const next = await api<SessionUser>('/api/auth/roles/', { method: 'POST', body: { ...body, role: target, account_type: accountType } });
        auth.setAuthenticatedUser(next);
      }
      finish(target);
      setTarget(null);
    } catch (failure) { setError(failure instanceof Error ? failure.message : label('Не удалось сохранить профиль.', 'Could not save profile.')); }
    finally { setBusy(false); }
  }
  async function sendCode() {
    setBusy(true); setError('');
    try {
      await api('/api/auth/sms/send/', { method: 'POST', body: { phone: auth.user?.phone, purpose: 'register' } });
      setCodeSent(true); setRetryAt(Date.now() + 60000); setNow(Date.now());
    } catch { setError(label('Не удалось отправить код. Попробуйте позже.', 'Could not send the code. Please try later.')); }
    finally { setBusy(false); }
  }
  async function verifyCode() {
    setBusy(true); setError('');
    try {
      await api('/api/auth/sms/verify/', { method: 'POST', body: { phone: auth.user?.phone, purpose: 'register', code } });
      await auth.refresh();
    } catch { setError(label('Неверный или устаревший код.', 'Invalid or expired code.')); }
    finally { setBusy(false); }
  }
  const targetProfile = target ? profiles?.[target] : null;
  const source = targetProfile ?? profiles?.[auth.user?.active_role === 'carrier' ? 'carrier' : 'customer'];
  const fields = accountType === 'company'
    ? [['company_name', label('Название компании', 'Company name')], ['contact_name', label('Контактное лицо', 'Contact name')]] as const
    : [['first_name', label('Имя', 'First name')], ['last_name', label('Фамилия', 'Last name')]] as const;
  const needsPhone = Boolean(target && !targetProfile && !auth.user?.phone_verified_at);
  const remaining = Math.max(0, Math.ceil((retryAt - now) / 1000));

  return <section id="settings-role" className="lovo-role-card" data-i18n-skip aria-busy={busy}>
    <h2><ArrowLeftRight size={20} aria-hidden />{label('Роль аккаунта', 'Account role')}</h2>
    {createRequest ? <p>{label('Заявки создаются в роли заказчика. Переключитесь, чтобы продолжить.', 'Requests are created as a customer. Switch roles to continue.')}</p> : null}
    <div className="lovo-role-card__modes" aria-label={label('Выберите роль', 'Choose role')}>
      {(['customer', 'carrier'] as const).map((role) => <button key={role} type="button" aria-pressed={(target ?? auth.user?.active_role) === role} disabled={!profiles || busy || (!target && auth.user?.active_role === role && !createRequest)} onClick={() => void choose(role)}>
        {role === 'customer' ? <UserRound size={20} aria-hidden /> : <Truck size={20} aria-hidden />}
        {role === 'customer' ? label('Заказчик', 'Customer') : label('Перевозчик', 'Carrier')}
      </button>)}
    </div>
    {!profiles && !error ? <p role="status">{label('Загрузка профилей…', 'Loading profiles…')}</p> : null}
    {error ? <p role="alert">{error}</p> : null}
    {!profiles && error ? <Button kind="secondary" onClick={() => void loadProfiles()}>{label('Повторить', 'Retry')}</Button> : null}
    {target ? <form onSubmit={save} className="lovo-role-card__form">
      <p>{target === 'customer' ? label('Чтобы создавать заявки, заполните профиль заказчика.', 'Complete your customer profile to create requests.') : label('Заполните профиль перевозчика. Для откликов потребуется верификация.', 'Complete your carrier profile. Verification is required to send offers.')}</p>
      {!targetProfile ? <div className="lovo-role-card__modes" aria-label={label('Тип профиля', 'Profile type')}>
        <button type="button" aria-pressed={accountType === 'person'} disabled={busy} onClick={() => setAccountType('person')}>{label('Частное лицо', 'Individual')}</button>
        <button type="button" aria-pressed={accountType === 'company'} disabled={busy} onClick={() => setAccountType('company')}>{label('Компания', 'Company')}</button>
      </div> : null}
      {fields.map(([key, title]) => source?.[key]?.trim() ? <p className="lovo-role-card__known" key={key}>{title}: <strong>{source[key]}</strong></p> : <label key={key}>{title}<input required maxLength={key === 'first_name' || key === 'last_name' ? 120 : 255} value={names[key]} disabled={busy} onChange={(event) => setNames({ ...names, [key]: event.target.value })} /></label>)}
      {needsPhone ? <div className="lovo-role-card__phone">
        <p>{label('Перед добавлением роли подтвердите телефон:', 'Verify your phone before adding a role:')} {auth.user?.phone || label('укажите телефон в профиле', 'add a phone in your profile')}</p>
        {auth.user?.phone ? <>
          <Button kind="secondary" disabled={busy || remaining > 0} onClick={() => void sendCode()}>{remaining ? `${label('Повторить через', 'Retry in')} ${remaining} ${label('с', 's')}` : label('Получить код', 'Send code')}</Button>
          {codeSent ? <><label>{label('Код из SMS', 'SMS code')}<input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} /></label><Button kind="secondary" disabled={busy || code.length !== 6} onClick={() => void verifyCode()}>{label('Подтвердить телефон', 'Verify phone')}</Button></> : null}
        </> : <a href={auth.user?.active_role === 'carrier' ? '#carrier-profile-internal' : '#profile-internal'}>{label('Открыть профиль', 'Open profile')}</a>}
      </div> : null}
      <div className="lovo-role-card__actions"><Button type="submit" disabled={busy || needsPhone}>{label('Сохранить и переключиться', 'Save and switch')}</Button><Button kind="secondary" disabled={busy} onClick={() => { setTarget(null); setError(''); }}>{label('Отменить', 'Cancel')}</Button></div>
    </form> : null}
  </section>;
}
